import { useCallback, useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import useTwilioCall from 'hooks/useTwilioCall';

// While a call is active we keep the provider pinned to the patient they're
// calling. Any nav attempt that leaves /clinic/{clinicId}/patient/{patientId}
// (including the browser back button) is intercepted by useBlocker and
// surfaced as a dialog. Nav *within* that patient is allowed so they can move
// between tabs (vitals → protocols → care plan, etc).
const CallNavigationGuard = () => {
    const { isActive, clinicId, patientId } = useTwilioCall();

    const shouldBlock = useCallback(
        ({ currentLocation, nextLocation }) => {
            if (!isActive || !clinicId || !patientId) {
                return false;
            }
            if (currentLocation.pathname === nextLocation.pathname) {
                return false;
            }
            const patientPrefix = `/clinic/${clinicId}/patient/${patientId}`;
            if (nextLocation.pathname === patientPrefix || nextLocation.pathname.startsWith(`${patientPrefix}/`)) {
                return false;
            }
            return true;
        },
        [isActive, clinicId, patientId]
    );

    const blocker = useBlocker(shouldBlock);

    // If the call ends while a navigation is pending in the blocker, release it
    // automatically — there's nothing left to guard against.
    useEffect(() => {
        if (blocker.state === 'blocked' && !isActive) {
            blocker.proceed();
        }
    }, [blocker, isActive]);

    // useBlocker only catches SPA navigations. Pair it with beforeunload so
    // closing the tab / reloading / typing a new URL also prompts the browser's
    // native confirmation while a call is in progress.
    useEffect(() => {
        if (!isActive) {
            return undefined;
        }
        const onBeforeUnload = (event) => {
            event.preventDefault();
            // Required for Chrome. Modern browsers ignore the custom string and
            // show their own message, but returnValue must be set to trigger it.
            event.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [isActive]);

    const open = blocker.state === 'blocked';

    const onStay = () => {
        blocker.reset();
    };

    return (
        <Dialog open={open} onClose={onStay} aria-labelledby="call-nav-guard-title">
            <DialogTitle id="call-nav-guard-title">You&apos;re on an active call</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You can&apos;t navigate away from this patient while a call is in progress. Hang up the call first, or stay here and
                    continue.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onStay} autoFocus variant="contained">
                    Stay on patient
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CallNavigationGuard;
